/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-06 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, TextStyle, Link, Button, Stack } from '@shopify/polaris';

import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';
import { SWATCH_CHANGE_IMAGE, SWATCH_SHAPE } from 'src/constants/theme-settings';
import { URL } from 'src/constants/links';
import { buildOptionList } from 'src/helpers';

type Props = WithI18nProps;

type State = {
  showSwatchByColorSection: boolean,
  showSwatchByImageSection: boolean,
  showSwatchByProductImageSection: boolean,
  showSwatchByTextSection: boolean,
  swatchChangeImg: Array<{ value: string, label: string }>,
  swatchShape: Array<{ value: string, label: string }>
};

class SwatchSettingsTab extends Component<Props, State> {
  state = {
    showSwatchByColorSection: true,
    showSwatchByImageSection: true,
    showSwatchByProductImageSection: true,
    showSwatchByTextSection: true,
    swatchChangeImg: [],
    swatchShape: []
  };

  componentDidMount() {
    const { i18n } = this.props;

    const swatchChangeImg = buildOptionList(SWATCH_CHANGE_IMAGE, i18n);
    const swatchShape = buildOptionList(SWATCH_SHAPE, i18n);
    this.setState({
      swatchChangeImg,
      swatchShape
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n } = nextProps;
    const { i18n: prevI18n } = this.props;

    if (i18n.locale !== prevI18n.locale) {
      const swatchChangeImg = buildOptionList(SWATCH_CHANGE_IMAGE, i18n);
      const swatchShape = buildOptionList(SWATCH_SHAPE, i18n);
      this.setState({
        swatchChangeImg,
        swatchShape
      });
    }
  }

  render() {
    const {
      i18n,
      updateField,
      optionsListData,
      swatchEnable,
      swatchByTextBg,
      swatchByTextColor,
      swatchByTextBorder,
      swatchByColorApply,
      swatchByImgApply,
      swatchByProImgApply,
      swatchByTextApply
    } = this.props;
    const {
      showSwatchByColorSection,
      showSwatchByImageSection,
      showSwatchByProductImageSection,
      showSwatchByTextSection,
      swatchChangeImg,
      swatchShape
    } = this.state;

    return (
      <FormLayout>
        <TextStyle variation="subdued">
          {i18n.translate('AdminHTML.DefaultThemeSettings.SwatchSettingsTab.helperText', {
            t1: (
              <Link external url={`${URL['DefaultThemeSettingsDoc']}#a2.3`}>
                {i18n.translate('AdminHTML.DefaultThemeSettings.SwatchSettingsTab.helperText_t1')}
              </Link>
            )
          })}
        </TextStyle>
        <Field
          name="products.swatchEnable"
          component={RenderToggleSwitch}
          label={i18n.translate('AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field1_Label')}
        />
        {swatchEnable && (
          <div className="groupFields-content">
            <Field
              name="products.showSwatchTooltip"
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field2_Label'
              )}
            />

            <div className="render-choice-flex-3">
              <Field
                name="products.swatchChangeImg"
                component={RenderChoice}
                label={i18n.translate(
                  'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field3_Label'
                )}
                choices={swatchChangeImg}
              />
            </div>

            {/** Swatch by color */}
            <div className="groupFields-label">
              <Button
                disclosure={showSwatchByColorSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showSwatchByColorSection')}>
                <RenderLabelTooltip
                  label={
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByColor_Label'
                      )}
                    </TextStyle>
                  }
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByColor_Tooltip'
                  )}
                />
              </Button>
            </div>

            {showSwatchByColorSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-2rem' }}>
                  <Stack vertical>
                    <Field
                      name="products.swatchByColorApply"
                      component={RenderAutocomplete}
                      value={swatchByColorApply}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field4_Label'
                      )}
                      options={optionsListData}
                      placeholder={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field4_Placeholder'
                      )}
                    />
                    <div className="render-choice-flex-3">
                      <Field
                        name="products.swatchByColorShape"
                        component={RenderChoice}
                        label={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field5_Label'
                        )}
                        choices={swatchShape}
                      />
                    </div>
                  </Stack>
                </div>
              </div>
            )}

            <div className="breakLine" />

            {/** Swatch by image */}
            <div className="groupFields-label">
              <Button
                disclosure={showSwatchByImageSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showSwatchByImageSection')}>
                <RenderLabelTooltip
                  label={
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByImage_Label'
                      )}
                    </TextStyle>
                  }
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByImage_Tooltip'
                  )}
                />
              </Button>
            </div>

            {showSwatchByImageSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-2rem' }}>
                  <Stack vertical>
                    <Field
                      name="products.swatchByImgApply"
                      component={RenderAutocomplete}
                      value={swatchByImgApply}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field6_Label'
                      )}
                      options={optionsListData}
                      placeholder={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field6_Placeholder'
                      )}
                    />
                    <div className="render-choice-flex-3">
                      <Field
                        name="products.swatchByImgShape"
                        component={RenderChoice}
                        label={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field7_Label'
                        )}
                        choices={swatchShape}
                      />
                    </div>
                  </Stack>
                </div>
              </div>
            )}

            <div className="breakLine" />

            {/** Swatch by product image */}
            <div className="groupFields-label">
              <Button
                disclosure={showSwatchByProductImageSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showSwatchByProductImageSection')}>
                <RenderLabelTooltip
                  label={
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByProductImage_Label'
                      )}
                    </TextStyle>
                  }
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByProductImage_Tooltip'
                  )}
                />
              </Button>
            </div>

            {showSwatchByProductImageSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-2rem' }}>
                  <Stack vertical>
                    <Field
                      name="products.swatchByProImgApply"
                      component={RenderAutocomplete}
                      value={swatchByProImgApply}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field8_Label'
                      )}
                      options={optionsListData}
                      placeholder={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field8_Placeholder'
                      )}
                    />
                    <div className="render-choice-flex-3">
                      <Field
                        name="products.swatchByProImgShape"
                        component={RenderChoice}
                        label={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field9_Label'
                        )}
                        choices={swatchShape}
                      />
                    </div>
                  </Stack>
                </div>
              </div>
            )}

            <div className="breakLine" />

            {/** Swatch by text */}
            <div className="groupFields-label">
              <Button
                disclosure={showSwatchByTextSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showSwatchByTextSection')}>
                <RenderLabelTooltip
                  label={
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByText_Label'
                      )}
                    </TextStyle>
                  }
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.gFSwatchByText_Tooltip'
                  )}
                />
              </Button>
            </div>

            {showSwatchByTextSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-2rem' }}>
                  <Stack vertical>
                    <Field
                      name="products.swatchByTextApply"
                      component={RenderAutocomplete}
                      value={swatchByTextApply}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field10_Label'
                      )}
                      options={optionsListData}
                      placeholder={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field10_Placeholder'
                      )}
                    />
                    <div className="render-choice-flex-3">
                      <Field
                        name="products.swatchByTextShape"
                        component={RenderChoice}
                        label={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field11_Label'
                        )}
                        choices={swatchShape}
                      />
                    </div>
                    <div className="groupFields-flex-2">
                      <RenderColorPickerHexPopover
                        colorValue={swatchByTextBg}
                        fieldName="products.swatchByTextBg"
                        fieldLabel={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field12_Label'
                        )}
                        updateField={updateField}
                      />

                      <RenderColorPickerHexPopover
                        colorValue={swatchByTextColor}
                        fieldName="products.swatchByTextColor"
                        fieldLabel={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field13_Label'
                        )}
                        updateField={updateField}
                      />
                    </div>
                    <div className="groupFields-flex-2">
                      <RenderColorPickerHexPopover
                        colorValue={swatchByTextBorder}
                        fieldName="products.swatchByTextBorder"
                        fieldLabel={i18n.translate(
                          'AdminHTML.DefaultThemeSettings.SwatchSettingsTab.field14_Label'
                        )}
                        updateField={updateField}
                      />
                    </div>
                  </Stack>
                </div>
              </div>
            )}
          </div>
        )}
      </FormLayout>
    );
  }

  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapStateToProps = (state, props) => {
  const defaultThemeSettingsForm = getFormValues('DefaultThemeSettingsForm')(state);
  const { defaultThemeSettings } = state;

  let colorList = {};
  ['swatchByTextBg', 'swatchByTextColor', 'swatchByTextBorder'].map(item => {
    colorList[item] = _.get(defaultThemeSettingsForm, `products.${item}`, '#FFFFFF');
    return '';
  });

  return {
    ...colorList,
    loading: _.get(defaultThemeSettings, 'loadingDefaultThemeSettings', false),
    swatchEnable: _.get(defaultThemeSettingsForm, 'products.swatchEnable', false),
    swatchByColorApply: _.get(defaultThemeSettingsForm, 'products.swatchByColorApply', false),
    swatchByImgApply: _.get(defaultThemeSettingsForm, 'products.swatchByImgApply', false),
    swatchByProImgApply: _.get(defaultThemeSettingsForm, 'products.swatchByProImgApply', false),
    swatchByTextApply: _.get(defaultThemeSettingsForm, 'products.swatchByTextApply', false),
    optionsListData: _.get(state.filterData.filterData, 'optionsListData', [])
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SwatchSettingsTab));
